import { useState } from "react"
import { Loader } from "@/UI"
import { useDeliveries } from "@/hooks/useDeliveries"
import { DestinationStatuses, Location, LocationDetailsType, StopType } from "@/types/orders.types"
import { EditOutlined } from "@ant-design/icons"
import { Popover, message } from "antd"
import { v4 as uuidv4 } from "uuid"
import { useTranslation } from "react-i18next"
import LuxonDatePicker from "../LuxonDatePicker"
import { DateTime } from "luxon"
import React from "react"

const { TimePicker } = LuxonDatePicker

interface EditDropoffTimePickerProps {
  orderId: string
  visible: boolean
  recipients: Location[]
  dropoffIndex: number
}

export const EditDropoffTimePicker: React.FC<EditDropoffTimePickerProps> = ({
  orderId,
  visible,
  recipients,
  dropoffIndex,
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { updateDeliveryRecipients } = useDeliveries()
  const recipient = recipients[dropoffIndex]
  const dropoffTime =
    recipient?.status === DestinationStatuses.DELIVERED
      ? recipient.finishedAt || recipient.dropoffEta
      : null
  if (!dropoffTime || !visible) return null

  const onDateSelected = async (date: DateTime | null, datesStrings: string | string[]) => {
    try {
      setLoading(true)
      const parsedDate =
        typeof datesStrings === "string"
          ? DateTime.fromFormat(datesStrings, "HH:mm")
          : DateTime.fromFormat(datesStrings?.[0]!, "HH:mm")
      let initialRecipients = [...recipients]
      if (!initialRecipients[dropoffIndex]?.location) return

      initialRecipients[dropoffIndex] = {
        ...initialRecipients[dropoffIndex],
        finishedAt: date ? parsedDate.toMillis() : undefined,
        type: StopType.DROPOFF,
      }

      initialRecipients = initialRecipients.map((r) => ({
        ...r,
        location: {
          ...r.location,
          id: uuidv4(),
        } as LocationDetailsType,
      }))
      await updateDeliveryRecipients(orderId, initialRecipients)
      if (document.getElementsByClassName("ant-popover-open")[0]) {
        ;(document.getElementsByClassName("ant-popover-open")[0] as any).click()
      }
    } catch (error) {
      message.error("An error occurred while updating dropoff time")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Popover
      placement="bottom"
      trigger="click"
      content={
        <TimePicker
          placeholder={t("common.dateAndTime")}
          size="middle"
          className="w-40 xs:w-28"
          format={"HH:mm"}
          suffixIcon={loading ? <Loader fontSize={16} /> : <></>}
          onChange={onDateSelected}
          value={dropoffTime ? DateTime.fromMillis(dropoffTime) : DateTime.now()}
          hideDisabledOptions={true}
        />
      }
    >
      <EditOutlined />
    </Popover>
  )
}
